import { Component, OnInit, EventEmitter, Input, Output, Renderer2, ViewChild, ElementRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import * as Sentry from '@sentry/browser';
import { environment } from '@environment';

import { FeedbackApiService } from '@core/http/feedback-api.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { OrdersUtilService } from '@app/core/services/orders-util/orders-util.service';

import { FeedbackReviewDialogComponent } from '@shared/components/feedback/feedback-review-dialog/feedback-review-dialog.component';
import { OrderSummaryDialogComponent } from '@app/modules/manage-orders/order-summary-dialog/order-summary-dialog.component';
import { Subject, lastValueFrom } from 'rxjs';
import { GtmService } from '@core/services/analytics/gtm.service';
import { AbsPipe } from '@app/shared/pipes/abs.pipe';
import { RupeePipe } from '@app/shared/pipes/rupee.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PonchoUIModule } from '@poncho/ui';
import { BarRatingModule } from 'ngx-bar-rating';
import { FormsModule } from '@angular/forms';
import { DeliverySupportComponent } from '../delivery-support/delivery-support.component';

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        DeliverySupportComponent,
        FormsModule,
        BarRatingModule,
        PonchoUIModule,
        MatProgressSpinnerModule,
        RupeePipe,
        AbsPipe
    ]
})
export class FeedbackComponent implements OnInit {
    @ViewChild('loader') loader: ElementRef;
    @ViewChild('imagePreview') imagePreview: ElementRef;
    @Input() order: any;
    @Input() openFeedback: boolean;
    @Output() close = new EventEmitter();
    DEPLOY_URL = environment.DEPLOY_URL;

    orderItemsToShow: any;
    showFoodRatingError = false;

    feedbackCreateSub$;

    themeClass = 'eatclub-theme';

    // uncontrolled formData
    feedbackFormData = {
        delivery_rating: 0,
        food_rating: 0,
        food_review: ''
    };

    ratingMessages = {
        default: 'Our brand managers will directly get your feedback. Please take 10 seconds to tell us more. ',
        less: 'We will work hard to fix the issue. Our brand managers will directly get your feedback. Please tell us more.',
        more: 'YAY! Please make our day by telling us what we did right?'
    };

    reviewHeadingTitle = this.ratingMessages['default'];
    placeholder = 'Please specify in detail';
    submitFeedbackInProgress = false;

    feedbackSavedObj = {
        saved: false,
        markedDelivery: false
    };
    PCDCSavingstext = '';
    numberOfImages = 0;
    maxImageToUpload = 3; //max 3images can be uploaded
    uploadedImagesKey = [];
    destroySubject$: Subject<void> = new Subject();

    constructor(
        private dialog: MatDialog,
        private feedbackApiService: FeedbackApiService,
        private toastrService: ToastrService,
        private ordersUtil: OrdersUtilService,
        private renderer: Renderer2,
        private gtmService: GtmService
    ) {}

    ngOnInit() {
        try {
            this.orderItemsToShow =
                this.order.order_details.items.length > 2
                    ? this.order.order_details.items.slice(0, 2)
                    : this.order.order_details.items;
            this.order.currentStatus = this.order.currentStatus || this.order.order_status.status;
            this.themeClass = environment.brand.id === 19 ? 'eatclub-theme' : 'custom-theme';
            this.PCDCSavingstext = this.ordersUtil.getPCDCtext(this.order.order_details);
        } catch (err) {
            Sentry.captureException(err);
        }
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    getItemCost(item) {
        let item_cost = item.cost;
        for (const key in item.customizations) {
            if (item.customizations.hasOwnProperty(key)) {
                item_cost += parseInt(item.customizations[key].cost, 10);
            }
        }
        return item_cost;
    }

    changeRating() {
        if (this.feedbackFormData.food_rating && this.feedbackFormData.delivery_rating) {
            if (this.feedbackFormData.food_rating < 3 || this.feedbackFormData.delivery_rating < 3) {
                this.reviewHeadingTitle = this.ratingMessages['less'];
            } else {
                this.reviewHeadingTitle = this.ratingMessages['more'];
            }
        }
    }

    checkFeedback() {
        if (
            !this.feedbackFormData.food_rating ||
            !this.feedbackFormData.delivery_rating ||
            this.feedbackFormData.food_rating === 0 ||
            this.feedbackFormData.delivery_rating === 0
        ) {
            this.showFoodRatingError = true;
            return;
        } else if (this.feedbackFormData.delivery_rating < 4 || this.feedbackFormData.food_rating < 4) {
            if (
                !this.feedbackFormData.food_review ||
                (this.feedbackFormData.food_review && this.feedbackFormData.food_review.length === 0)
            ) {
                const dialogConfig = new MatDialogConfig();
                dialogConfig.panelClass = 'feedback-review-dialog';
                const dialogRef = this.dialog.open(FeedbackReviewDialogComponent, dialogConfig);

                dialogRef.afterClosed().subscribe(data => {
                    if (data) {
                        this.feedbackFormData.food_review = data;
                    }
                    this.submitFeedback();
                });
            } else {
                this.submitFeedback();
            }
        } else {
            this.submitFeedback();
        }
    }

    submitFeedback() {
        const { delivery_rating, food_rating, food_review } = this.feedbackFormData;

        // creating api data
        const apiData: any = {};
        apiData.experience_rating = Number(delivery_rating);
        apiData.food_rating = Number(food_rating);
        apiData.food_feedback = [];

        if (food_review) apiData.food_feedback.push(`Review = ${food_review}`);

        apiData.food_feedback = apiData.food_feedback.join(' | ');

        apiData.image_urls = JSON.stringify(this.uploadedImagesKey);

        this.submitFeedbackInProgress = true;

        if (this.openFeedback) {
            this.feedbackCreateSub$ = this.feedbackApiService.createOpen(this.order.order_details.tracking_id, apiData);
        } else {
            this.feedbackCreateSub$ = this.feedbackApiService.create(this.order.order_details.tracking_id, apiData);
        }

        this.feedbackCreateSub$.subscribe(
            (data: any) => {
                this.submitFeedbackInProgress = false;
                this.feedbackSavedObj.saved = true;
                this.close.emit(this.feedbackSavedObj);
                this.toastrService.openSnackBar(data.meta.message, 'OKAY');
            },
            (errorResponse: any) => {
                this.submitFeedbackInProgress = false;
                this.close.emit(this.feedbackSavedObj);
                if (errorResponse.error && errorResponse.error.meta) {
                    this.toastrService.openSnackBar(errorResponse.error.meta.message, 'OKAY');
                } else {
                    this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                }
            }
        );
    }

    closeContainer() {
        this.close.emit(this.feedbackSavedObj);
    }

    openOrderDetailsModal() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = ['feedback-dialog', 'order-details'];
        dialogConfig.data = {
            order: this.order
        };
        dialogConfig.autoFocus = false;
        this.dialog.open(OrderSummaryDialogComponent, dialogConfig);
    }

    selectImage(target) {
        const file = target.files;
        const allowedFileSize = 25;
        const fileSize = Math.round(file[0].size / (1024 * 1024)); //in MB
        if (fileSize > allowedFileSize) {
            this.gtmService.logEvent('js_errors', {
                event_label: 'Uploaded Image Size',
                event_value: fileSize
            });
            this.toastrService.openSnackBar('File Size should be less than 25MB', 'OKAY');
        } else {
            this.handleFileUpload(fileSize, file[0]);
        }
    }

    handleFileUpload(fileSize, file) {
        const { type } = file;
        this.createMockLoader();
        lastValueFrom(this.feedbackApiService.getSignedUrl(fileSize, type)).then(
            (resp: any) => {
                if (resp.meta.code === 200) {
                    const { upload_url, key } = resp.data;
                    lastValueFrom(this.feedbackApiService.pushImageToS3(upload_url, file))
                        .then(() => {
                            this.renderImageUI(file, key);
                        })
                        .catch(error => {
                            console.log(error);
                            this.gtmService.logEvent('js_errors', {
                                event_label: 'Error - Uploading Image',
                                event_value: error?.message || 'Forbidden'
                            });
                            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                        })
                        .finally(() => {
                            this.deleteLoader();
                        });
                } else {
                    this.deleteLoader();
                    this.toastrService.openSnackBar(resp.meta.message, 'OKAY');
                }
            },
            errorResponse => {
                this.deleteLoader();
                if (errorResponse && errorResponse.meta) {
                    this.toastrService.openSnackBar(errorResponse.meta.message, 'OKAY');
                } else {
                    this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                }
            }
        );
    }

    createMockLoader() {
        const imagePreviewTag = this.imagePreview.nativeElement;
        const parentDiv = this.loader.nativeElement;
        this.renderer.setStyle(parentDiv, 'display', 'block');
        this.renderer.appendChild(imagePreviewTag, parentDiv);
    }

    deleteLoader() {
        const imagePreviewTag = this.imagePreview.nativeElement;
        const parentDiv = this.loader.nativeElement;
        this.renderer.removeChild(imagePreviewTag, parentDiv);
    }

    renderImageUI(file, key) {
        this.numberOfImages++;
        this.uploadedImagesKey.push(key); // store key
        const imagePreviewTag = this.imagePreview.nativeElement;
        const parentDiv = this.renderer.createElement('div');
        this.renderer.addClass(parentDiv, 'parent-container');
        this.renderer.setAttribute(parentDiv, 'id', key); //using key as element id

        const img = this.renderer.createElement('img');
        this.renderer.addClass(img, 'img-preview');
        this.renderer.setAttribute(img, 'src', URL.createObjectURL(file));
        this.renderer.appendChild(parentDiv, img);

        const crossImg = this.renderer.createElement('img');
        this.renderer.addClass(crossImg, 'cross-icon');
        this.renderer.setAttribute(crossImg, 'src', '/assets/images/ic_cross.svg');
        this.renderer.appendChild(parentDiv, crossImg);
        this.renderer.listen(crossImg, 'click', () => {
            this.deleteImage(imagePreviewTag, parentDiv);
        });

        this.renderer.appendChild(imagePreviewTag, parentDiv);
    }

    deleteImage(parent, child) {
        this.numberOfImages--;
        this.uploadedImagesKey = this.uploadedImagesKey.filter(data => data != child.id); //remove key upon deletion
        this.renderer.removeChild(parent, child);
    }
}
